import React, { useMemo, useState, useEffect } from 'react';
import { format } from 'date-fns';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store';
import { authenticateUserFromStorage } from '../../../features/sessions/sessionSlice';
import * as REQUEST_REQUIREMENTS from '../../../api/requestRequirements';
import { getProjectContents, manageProjectContentsSliceActions } from '../../../features/sessions/projectsManagement/sessionManageProject';
import { ProjectPlayer } from '../../audioPlayer/WaveSurfer/projectPlayer/ProjectPlayer';
import { deleteProject, MasterFiles } from '../../../features/sessions/projectsManagement/sessionManageProjects';
import LoadingModal from '../loadingModal/LoadingModal';
import { YesNoDialogModal } from '../dialogBox/group/yesNo/YesNoDialogModal';
import {
    Box, Button, Chip, Dialog, DialogActions, DialogContent,
    DialogTitle, IconButton, Stack, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CallMergeIcon from '@mui/icons-material/CallMerge';

const BG_DIALOG      = '#1c1c24';
const BRAND_RED      = '#c0392b';
const TEXT_PRIMARY   = '#f4f4f6';
const TEXT_SECONDARY = 'rgba(244,244,246,0.65)';
const BORDER_SUBTLE  = '1px solid rgba(255,255,255,0.08)';

export default function ProjectDetailsModal({ projectInfoAndUrls, close }: any) {
    const authHeaders   = useSelector((state: RootState) => state.session.authHeaders);
    const currentUser   = useSelector((state: RootState) => state.session.currentUser);
    const userValidated = useSelector((state: RootState) => state.session.loggedIn);
    const loading       = useSelector((state: RootState) =>
        state.sessionProjectPlayerManagement.loading || state.sessionManageProjectContents.loading);

    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const { userNicknameRoute } = useParams();

    const [deleteClicked, setDeleteClicked] = useState(false);
    const MemorizedProjectPlayer = useMemo(() => React.memo(ProjectPlayer), []);

    useEffect(() => {
        async function authenticateUser() {
            if (!userValidated) await dispatch(authenticateUserFromStorage());
        }
        authenticateUser();
    }, []);

    function handleFormatDate(date: any) {
        return format(new Date(date), 'dd MMM yyyy HH:mm:ss');
    }

    async function handleGetProjectContents(projectSelected: any) {
        manageProjectContentsSliceActions.loadingPage();
        const response = await dispatch(getProjectContents({
            authHeaders,
            userNickname: projectSelected.user.nickname,
            projectId: projectSelected.id,
        }));
        if (response) {
            const PRIVATE_ROUTES = REQUEST_REQUIREMENTS.handlePrivateRoutes({
                ROUTE_PARAMS: { userNickname: projectSelected.user.nickname, projectId: projectSelected.id },
            });
            if (PRIVATE_ROUTES) navigate(PRIVATE_ROUTES.PROJECT_CONTENTS);
        }
    }

    async function handleDeleteProject() {
        try {
            if (!authHeaders || !currentUser?.nickname) throw new Error('Some request information is missing.');
            const response = await dispatch(deleteProject({
                authHeaders,
                urlParams: { currentUserNickname: currentUser.nickname, projectId: projectInfoAndUrls.project.id },
            }));
            if (response.meta.requestStatus === 'fulfilled') close(true);
        } catch (error) {
            alert((error as Error).message || 'An error occurred while deleting the project.');
        }
    }

    const project = projectInfoAndUrls?.project;

    return (
        <>
            {loading && <LoadingModal />}

            {deleteClicked && (
                <YesNoDialogModal
                    message='You will delete this project. Are you sure?'
                    onYesClick={handleDeleteProject}
                    onCloseClick={setDeleteClicked}
                />
            )}

            <Dialog
                open={true}
                onClose={close}
                fullWidth
                maxWidth='sm'
                PaperProps={{ sx: { bgcolor: BG_DIALOG, color: TEXT_PRIMARY, border: BORDER_SUBTLE } }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', pb: 1 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                        <Typography variant='h6' fontWeight={700} color={TEXT_PRIMARY}>
                            {project?.title}
                        </Typography>
                        {project?.createdAt && (
                            <Typography variant='caption' color={TEXT_SECONDARY}>
                                {handleFormatDate(project.createdAt)}
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1, flexShrink: 0 }}>
                        <Chip
                            label={project?.privacity === 0 ? 'PUBLIC' : 'PRIVATE'}
                            size='small'
                            sx={{
                                bgcolor: project?.privacity === 0 ? 'rgba(35,148,0,0.18)' : 'rgba(192,57,43,0.18)',
                                color: project?.privacity === 0 ? '#4caf50' : '#e57373',
                                fontWeight: 600, fontSize: '0.65rem',
                            }}
                        />
                        <IconButton size='small' onClick={close} sx={{ color: TEXT_SECONDARY }}>
                            <CloseIcon fontSize='small' />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ pt: 1.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Description */}
                    {project?.description && (
                        <Typography variant='body2' color={TEXT_SECONDARY} sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                            {project.description}
                        </Typography>
                    )}

                    {/* Stats row */}
                    {project?.updatedAt && (
                        <Stack direction='row' spacing={3}>
                            <Box>
                                <Typography variant='caption' color={TEXT_SECONDARY} display='block'>
                                    Updated At
                                </Typography>
                                <Typography variant='caption' color={TEXT_PRIMARY}>
                                    {handleFormatDate(project.updatedAt)}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant='caption' color={TEXT_SECONDARY} display='block'>
                                    Useful
                                </Typography>
                                <Typography variant='caption' color={TEXT_PRIMARY}>
                                    {project.nUsefulContents}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant='caption' color={TEXT_SECONDARY} display='block'>
                                    Other
                                </Typography>
                                <Typography variant='caption' color={TEXT_PRIMARY}>
                                    {project.nUselessContents}
                                </Typography>
                            </Box>
                        </Stack>
                    )}

                    {/* Master files */}
                    {projectInfoAndUrls?.masterFiles?.length > 0 ? (
                        <Box>
                            <Typography variant='caption' color={TEXT_SECONDARY} sx={{ mb: 1, display: 'block' }}>
                                Versions
                            </Typography>
                            <Stack spacing={1.5}>
                                {projectInfoAndUrls.masterFiles.map((item: MasterFiles, index: number) => (
                                    <Box key={index}>
                                        <Typography variant='caption' color={TEXT_SECONDARY} sx={{ mb: 0.5, display: 'block' }}>
                                            {handleFormatDate(item.createdAt)}
                                        </Typography>
                                        <MemorizedProjectPlayer fileUrl={`${item.signedUrl}`} />
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                    ) : (
                        <Typography variant='body2' color={TEXT_SECONDARY} textAlign='center' sx={{ py: 2 }}>
                            This project doesn&apos;t have versions yet
                        </Typography>
                    )}
                </DialogContent>

                <DialogActions sx={{ borderTop: BORDER_SUBTLE, px: 2, py: 1.5, justifyContent: 'space-between' }}>
                    {currentUser?.id === project?.userId ? (
                        <IconButton
                            onClick={() => setDeleteClicked(true)}
                            sx={{ color: 'rgba(255,100,100,0.8)', '&:hover': { color: '#ff5555' } }}
                        >
                            <DeleteOutlineIcon />
                        </IconButton>
                    ) : (
                        <Box />
                    )}
                    <Button
                        variant='outlined'
                        startIcon={<CallMergeIcon />}
                        onClick={() => handleGetProjectContents(project)}
                        sx={{
                            borderColor: BRAND_RED,
                            color: BRAND_RED,
                            textTransform: 'none',
                            '&:hover': { bgcolor: 'rgba(192,57,43,0.08)', borderColor: BRAND_RED },
                        }}
                    >
                        Manage
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
